import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useLazyQuery } from '@apollo/client';
import { Breadcrumb, Button, message } from 'antd';
import sortBy from 'lodash/sortBy';

import generatePath from '~settings/routing';
import { updateHomepageSetting, queryHomepageSetting } from '../gql';

import DesktopSetting from './desktop';
import ModuleSettingModal from './module-setting';
import PopularProperties from './desktop/components/popular-properties';
import BudgetFriendlyPrivacy from './desktop/components/budget-friendly-privacy';
import SpecialOffer from './desktop/components/special-offer';
import UniversityPage from './desktop/components/university';
import NewRelease from './desktop/components/new-release';
import TipContentModal from './desktop/components/tip-content-modal';

export default function HomepageSetting({ match, location }) {
  const { t } = useTranslation();
  const [activeKey, setActiveKey] = useState(1);
  const [priceTip, setPriceTip] = useState();
  const [visible, setVisible] = useState(false);
  const [editVisible, setEditVisible] = useState(false);
  const [renderData, setRenderData] = useState([]);
  const cityName = match?.params?.name;
  const citySlug = location?.search?.split('slug=')[1];

  const components = {
    popularProperties: PopularProperties,
    budgetFriendlyPrivacy: BudgetFriendlyPrivacy,
    university: UniversityPage,
    specialOffer: SpecialOffer,
    newRelease: NewRelease,
  };

  const [queryDesktopSetting] = useLazyQuery(queryHomepageSetting, {
    fetchPolicy: 'no-cache',
    onCompleted: response => {
      const settingData = response.getHomepageSetting?.homepageSetting?.desktopSetting;
      const settingArray = sortBy(
        Object.keys(settingData).map((key, index) => {
          const DynamicComponent = components[key];
          const isBFP = key === 'budgetFriendlyPrivacy';
          if (isBFP) {
            setPriceTip(settingData[key]);
          }
          return {
            ...settingData[key],
            name: key,
            id: index,
            component: isBFP ? (
              <BudgetFriendlyPrivacy
                originalPriceTip={settingData[key]}
                t={t}
                setEditVisible={value => setEditVisible(value)}
              />
            ) : (
              <DynamicComponent />
            ),
          };
        }),
        ['position'],
      );
      setRenderData(settingArray);
    },
  });

  const [mutationHomepageSetting, { loading }] = useMutation(updateHomepageSetting, {
    fetchPolicy: 'no-cache',
    variables: {
      input: {
        citySlug,
        desktopSetting: {
          budgetFriendlyPrivacy: priceTip,
        },
      },
    },
    onCompleted: () => {
      handleQueryDesktopSetting();
      setEditVisible(false);
    },
    onError: () => {
      message.error(t('landlord:landlord.error.default'));
    },
  });

  const handleQueryDesktopSetting = () => {
    queryDesktopSetting({ variables: { citySlug } });
  };

  useEffect(() => {
    handleQueryDesktopSetting();
  }, []);

  return (
    <div className="homepage-setting-wrapper">
      <div className="breadcrumb-wrapper">
        <Breadcrumb>
          <Breadcrumb.Item className="link-font">
            <Link to={generatePath('strategy')}>
              {t('cms.dashboard.strategy.city_homepage.subtitle')}
            </Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item className="link-font">
            <Link
              to={generatePath('strategy.cityHomepage', { name: cityName }, { slug: citySlug })}
            >
              {cityName}
            </Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item className="link-font-active">
            {t('cms.dashboard.strategy.homepage_setting.homepage_setting')}
          </Breadcrumb.Item>
        </Breadcrumb>
        <div className="setting-line">
          <p className="setting-title">
            {t('cms.dashboard.strategy.homepage_setting.homepage_setting')}
          </p>
          <Button className="setting-button" onClick={() => setVisible(true)}>
            {t('cms.dashboard.strategy.homepage_setting.module_setting')}
          </Button>
        </div>
        <div className="setting-tabs">
          <div
            onClick={() => setActiveKey(1)}
            className={activeKey === 1 ? 'tab-item active' : 'tab-item'}
          >
            {t('cms.dashboard.strategy.homepage_setting.desktop')}
          </div>
          <div
            // onClick={() => setActiveKey(2)}
            className={activeKey !== 1 ? 'tab-item active' : 'tab-item'}
          >
            {t('cms.dashboard.strategy.homepage_setting.mobile_web_app')}
          </div>
        </div>
      </div>
      <If condition={activeKey === 1}>
        <DesktopSetting renderData={renderData} />
      </If>
      {renderData && (
        <ModuleSettingModal
          onClose={() => setVisible(false)}
          t={t}
          visible={visible}
          refetch={handleQueryDesktopSetting}
          data={renderData}
          setData={setRenderData}
          citySlug={citySlug}
        />
      )}
      {editVisible && (
        <TipContentModal
          onClose={() => setEditVisible(false)}
          visible={editVisible}
          t={t}
          setPriceTip={setPriceTip}
          priceTip={priceTip}
          loading={loading}
          mutationHomepageSetting={mutationHomepageSetting}
        />
      )}
    </div>
  );
}
